/**
 * Official icon system — Lucide (https://lucide.dev, ISC licence).
 * Icons are bundled as inline SVG (no runtime/network dependency), inherit
 * `currentColor`, and are referenced by kebab-case name from sector config.
 */
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Award,
  Baby,
  Banknote,
  BarChart3,
  Bed,
  BookOpen,
  Bot,
  Calendar,
  Brain,
  Building2,
  Calculator,
  Circle,
  CheckCircle2,
  CigaretteOff,
  CircleDollarSign,
  Coins,
  Divide,
  Droplet,
  Filter,
  Flag,
  FolderOpen,
  Gauge,
  Globe,
  GraduationCap,
  HardHat,
  Heart,
  HeartPulse,
  Info,
  Landmark,
  Laptop,
  Lightbulb,
  LineChart,
  Link2,
  Lock,
  LogIn,
  LogOut,
  Map,
  Minus,
  Moon,
  Percent,
  PieChart,
  PiggyBank,
  Printer,
  Scale,
  Search,
  School,
  Ship,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Smile,
  Stethoscope,
  Sun,
  Syringe,
  Target,
  Timer,
  TrendingDown,
  TrendingUp,
  User,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  X,
  Zap,
} from 'lucide-static';

const REGISTRY: Record<string, string> = {
  activity: Activity,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  award: Award,
  baby: Baby,
  banknote: Banknote,
  'bar-chart-3': BarChart3,
  bed: Bed,
  'book-open': BookOpen,
  bot: Bot,
  calendar: Calendar,
  brain: Brain,
  'building-2': Building2,
  calculator: Calculator,
  circle: Circle,
  'check-circle-2': CheckCircle2,
  'cigarette-off': CigaretteOff,
  'circle-dollar-sign': CircleDollarSign,
  coins: Coins,
  divide: Divide,
  droplet: Droplet,
  filter: Filter,
  flag: Flag,
  'folder-open': FolderOpen,
  gauge: Gauge,
  globe: Globe,
  'graduation-cap': GraduationCap,
  'hard-hat': HardHat,
  heart: Heart,
  'heart-pulse': HeartPulse,
  info: Info,
  landmark: Landmark,
  laptop: Laptop,
  lightbulb: Lightbulb,
  'line-chart': LineChart,
  'link-2': Link2,
  lock: Lock,
  'log-in': LogIn,
  'log-out': LogOut,
  map: Map,
  minus: Minus,
  moon: Moon,
  percent: Percent,
  'pie-chart': PieChart,
  'piggy-bank': PiggyBank,
  printer: Printer,
  scale: Scale,
  search: Search,
  school: School,
  ship: Ship,
  shield: Shield,
  'shield-check': ShieldCheck,
  'shopping-cart': ShoppingCart,
  smartphone: Smartphone,
  smile: Smile,
  stethoscope: Stethoscope,
  sun: Sun,
  syringe: Syringe,
  target: Target,
  timer: Timer,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  user: User,
  'user-check': UserCheck,
  'user-plus': UserPlus,
  users: Users,
  wallet: Wallet,
  x: X,
  zap: Zap,
};

/** All registered icon names (used by integrity tests). */
export const iconNames: string[] = Object.keys(REGISTRY);

/** Returns an inline SVG for a registered icon name. */
export function icon(name: string, cls = 'icon'): string {
  const svg = REGISTRY[name];
  if (!svg) {
    console.warn(`Unknown icon: ${name}`);
    return '';
  }
  return svg.replace('<svg', `<svg class="${cls}" aria-hidden="true" focusable="false"`);
}
